import TwinTrans from '../../../../../../../../../baseComponents/TwinTrans'
import { FormElement, FormRender } from '../../../../../../../../../forms/FormRenderer/types'
import ITHeaderText, { ITHeaderTextCustomer, ITHeaderTextOrder } from '../../../../../../../../../baseComponents/TwinTable/InputTableLayout/InputTableLeftColumn/Components/ITHeaderText'
import useTwinTranslation from '../../../../../../../../../utils/hooks/useTwinTranslation'
import { parseAddressToOneLine } from '../../../../../../../../../utils/globals/address'
import { useCallback, useMemo } from 'react'
import { returnClassOfSpanTemplate } from '../../../../../../../../../utils/globals/tailwind'
import { TwinDictionary } from '../../../../../../../../../utils/globals/dictionary'
import { displayDate } from '../../../../../../../../../utils/globals/date'
import { WorkSheetModelType, workSheetStatusInversed } from '@teinor/erp/types/company/order/sell/workSheet'
import { UserPermissions } from '../../../../../../../../../baseComponents/CreateRoutering/types'
import { sortEmployeesByUser } from '../../../../../../../../../utils/globals/employees'
import { getEmployees, getUserPermissions } from '../../../../../../../../../utils/reducers/getters'
import { TableData } from '../../../../../../../../../baseComponents/TwinTable/types'
import InputTableLeftColumnForm from '../../../../../../../../../baseComponents/TwinTable/InputTableLayout/InputTableLeftColumn/InputTableLeftColumnForm'
import { InputTableLeftColumnChange } from '../../../../../../../../../baseComponents/TwinTable/InputTableLayout/InputTableLeftColumn/types'
import { twinFetchPostJSON } from '../../../../../../../../../utils/globals/data'
import { useParams } from 'react-router-dom'
import { valueOrDefaultValue } from '../../../../../../../../../utils/globals/valueOrDefaultValue'
import locations from '../../../../../../../../../utils/globals/locations'
import { permissionCheck } from '../../../../../../../../../baseComponents/PermissionChecker/function'
import { InputWithLabelMargin } from '../../../../../../../../../forms/Input'
import AppStyleDecider from '../../../../../../../../../baseComponents/AppsStyleDecider'
import InputTableTopHeader from '../../../../../../../../../baseComponents/TwinTable/InputTableLayout/InputTableTopHeader'

interface WorkSheetHeaderProps {
    userPermissions: UserPermissions
    data: TwinDictionary
    getCompleteData: (inputtable?: TableData) => Promise<void>
}

const WorkSheetHeader: React.FC<WorkSheetHeaderProps> = ({ ...rest }) => {
    return <AppStyleDecider DefaultComponent={WorkSheetHeaderDefault} NewComponent={WorkSheetHeaderNew} {...rest} />
}

const WorkSheetHeaderDefault: React.FC<WorkSheetHeaderProps> = ({ ...rest }) => {
    return (
        <div className='flex flex-col input_table_left_column flex-auto '>
            <div className='regular18 flex items-center justify-between input_table_left_column_header'>
                <TwinTrans transKey={'dataWorkSheet'}>Datos de la hoja de trabajo</TwinTrans>
            </div>
            <div className='flex flex-auto h-1 input_table_left_column_content'>
                <WorkSheetHeaderInner {...{ ...rest }} />
            </div>
        </div>
    )
}

interface WorkSheetHeaderInnerProps extends WorkSheetHeaderInnerLogicProps {
    userPermissions: UserPermissions
    getCompleteData: (inputtable?: TableData) => Promise<void>
}

const WorkSheetHeaderInner: React.FC<WorkSheetHeaderInnerProps> = ({ data, userPermissions, getCompleteData }) => {
    const { t } = useTwinTranslation()
    const { memorizedAddresses, myHandleChange } = useWorkSheetHeaderInnerLogic({ data })
    if (!data) {
        return null
    }
    const employees = getEmployees()
    let extraFields: FormElement<keyof WorkSheetModelType>[] = []
    let representativeEmployeeSection: FormElement<keyof WorkSheetModelType>[] = []
    if (data.CustomerAddressId) {
        extraFields.push({
            name: 'CustomerAddressId',
            component: 'Custom',
            render: ({ value }) => {
                return <ITHeaderText label={t('fiscalAddress', 'Dirección Fiscal')} value={memorizedAddresses.customerAddress} className={returnClassOfSpanTemplate(5)} />
            },
            extraProps: {
                customerAddress: memorizedAddresses.customerAddress
            }
        })
    }
    if (parseInt(data.status) === workSheetStatusInversed['finished'] || data.active === false) {
        representativeEmployeeSection.push(
            {
                name: 'RepresentativeEmployeeId',
                component: 'Custom',
                render: ({ extraProps }) => <ITHeaderText label={t('representative', 'Representante')} value={extraProps.RepresentativeEmployeeName} className={returnClassOfSpanTemplate(5)} />,
                extraProps: {
                    RepresentativeEmployeeName: data?.RepresentativeEmployee?.fullname_short
                }
            },
        )
    } else {
        representativeEmployeeSection.push(
            {
                name: 'RepresentativeEmployeeId',
                component: 'CustomSelectWithSearchBar',
                label: t('representative', 'Representante'),
                items: employees || {},
                onChange: getCompleteData,
                className: returnClassOfSpanTemplate(5),
                sortItems: true,
                fieldName: 'fullname_short',
                sortFunction: (emp) => sortEmployeesByUser(emp, false),
            },
        )
    }
    const sections: FormRender<WorkSheetModelType> = [
        {
            cols: 5,
            elements: [
                {
                    name: 'CustomerId',
                    component: 'Custom',
                    render: () => <ITHeaderTextCustomer label={t('customer', 'Cliente')} headerData={data} className={returnClassOfSpanTemplate(5)} />
                },
                {
                    name: 'custom_name',
                    component: 'Custom',
                    render: (props) => {
                        return <InputWithLabelMargin name='custom_name' label={t('customName', 'Nombre personalizado')} onChange={props.onChange} defaultValue={data.custom_name} className={returnClassOfSpanTemplate(5)}/>
                    }
                },
                ...representativeEmployeeSection,
                {
                    name: 'processed_date',
                    component: 'Custom',
                    render: ({ extraProps }) => <ITHeaderText label={t('date', 'Fecha')} value={displayDate(new Date(extraProps.processed_date))} className={returnClassOfSpanTemplate(3)} />,
                    extraProps: {
                        processed_date: data?.processed_date
                    }
                },
                {
                    name: 'full_assigned_number',
                    component: 'Custom',
                    render: ({ extraProps }) => <ITHeaderText label={t('assignedN', 'Nº asignado')} value={extraProps.full_assigned_number} className={(extraProps.full_assigned_number ? '' : 'text-red-BA') + ' ' + returnClassOfSpanTemplate(2)} />,
                    extraProps: {
                        full_assigned_number: data?.full_assigned_number
                    }
                },
                {
                    name: 'WorkingCenterId',
                    component: 'Custom',
                    render: ({ value }) => <ITHeaderText label={t('soldBy', 'Vendido por')} value={data?.WorkingCenter?.name || ''} className={returnClassOfSpanTemplate(3)} />
                },
                {
                    name: 'BillingWorkingCenterId',
                    component: 'Custom',
                    render: ({ value }) => {
                        return <ITHeaderText label={t('billingBy', 'Facturado por')} value={data?.BillingWorkingCenter?.name || ''} className={returnClassOfSpanTemplate(3)} />
                    },
                },
                {
                    name: 'WorkingCenterCounterId',
                    component: 'Custom',
                    render: ({ value }) => {
                        return <ITHeaderText label={t('serie', 'Serie')} value={data?.WorkingCenterCounter?.serie || ''} className={returnClassOfSpanTemplate(2)} />
                    },
                },
                ...extraFields,
                {
                    name: 'WorkingCenterAddressId',
                    component: 'Custom',
                    render: ({ value, extraProps }) => {
                        return <ITHeaderText label={t('workingCenterAddress', 'Dirección centro de trabajo')} value={extraProps.workingCenterAddress} className={returnClassOfSpanTemplate(5)} />
                    },
                    extraProps: {
                        workingCenterAddress: memorizedAddresses.workingCenterAddress
                    }
                },
                {
                    name: 'SellOrderId',
                    component: 'Custom',
                    render: ({ extraProps }) => <ITHeaderTextOrder label={t('sellOrderN', 'Nº pedido')} className={returnClassOfSpanTemplate(2)} OrderId={extraProps.OrderId} full_asssigned_number={extraProps.full_assigned_number} orderUrl={locations.sellOrderSingle} readOnly={!permissionCheck(getUserPermissions('sell.order'), 'read')} />,
                    extraProps: {
                        full_assigned_number: data?.SellOrder?.full_assigned_number,
                        OrderId: data?.SellOrderId
                    }
                },
                {
                    name: 'SellDeliveryNoteId',
                    component: 'Custom',
                    render: ({ extraProps }) => <ITHeaderTextOrder label={t('sellDeliveryNoteN', 'Nº albarán')} className={returnClassOfSpanTemplate(2)} OrderId={extraProps.OrderId} full_asssigned_number={extraProps.full_assigned_number} orderUrl={locations.sellDeliveryNoteSingle} readOnly={!permissionCheck(getUserPermissions('sell.deliveryNote'), 'read')} />,
                    extraProps: {
                        full_assigned_number: data?.SellDeliveryNote?.full_assigned_number,
                        OrderId: data?.SellDeliveryNoteId
                    }
                },
                {
                    name: 'SellInvoiceId',
                    component: 'Custom',
                    render: ({ extraProps }) => <ITHeaderTextOrder label={t('sellInvoiceN', 'Nº factura')} className={returnClassOfSpanTemplate(2)} OrderId={extraProps.OrderId} full_asssigned_number={extraProps.full_assigned_number} orderUrl={locations.sellInvoiceSingle} readOnly={!permissionCheck(getUserPermissions('sell.invoice'), 'read')} />,
                    extraProps: {
                        full_assigned_number: data?.SellInvoice?.full_assigned_number,
                        OrderId: data?.SellInvoiceId
                    }
                },
            ]
        }
    ]
    const parsedFields = valueOrDefaultValue(sections, userPermissions, data)
    return (
        <div className='flex flex-auto flex-col'>
            <InputTableLeftColumnForm form={parsedFields} handleChange={myHandleChange} headerData={data.header} />
        </div>
    )
}

interface WorkSheetHeaderInnerLogicProps {
    data: TwinDictionary
}

const useWorkSheetHeaderInnerLogic = ({ data }: WorkSheetHeaderInnerLogicProps) => {
    const params = useParams<{ id: string }>()
    const WorkingCenterAddress = data?.WorkingCenterAddress
    const CustomerAddress = data?.CustomerAddress
    const memorizedAddresses = useMemo(() => {
        return {
            workingCenterAddress: parseAddressToOneLine(WorkingCenterAddress),
            customerAddress: parseAddressToOneLine(CustomerAddress)
        }
    }, [WorkingCenterAddress, CustomerAddress])

    const myHandleChange: InputTableLeftColumnChange = useCallback(async (_id, field, value) => {
        await twinFetchPostJSON('/api/app/order/sell/workSheet/updateWorkSheet', { id: params.id, [field]: value })
    }, [params.id])

    return { memorizedAddresses, myHandleChange }
}

const WorkSheetHeaderNew: React.FC<WorkSheetHeaderInnerProps> = ({ data, userPermissions, getCompleteData }) => {
    const { t } = useTwinTranslation()
    const { memorizedAddresses, myHandleChange } = useWorkSheetHeaderInnerLogic({ data })
    if (!data) {
        return null
    }
    const employees = getEmployees()
    let extraFields: FormElement<keyof WorkSheetModelType>[] = []
    let representativeEmployeeSection: FormElement<keyof WorkSheetModelType>[] = []
    if (data.CustomerAddressId) {
        extraFields.push({
            name: 'CustomerAddressId',
            component: 'Custom',
            render: ({ value }) => {
                return <ITHeaderText label={t('fiscalAddress', 'Dirección Fiscal')} value={memorizedAddresses.customerAddress} />
            },
            hideSectionByDefault: true,
            extraProps: {
                customerAddress: memorizedAddresses.customerAddress
            }
        })
    }
    if (parseInt(data.status) === workSheetStatusInversed['finished'] || data.active === false) {
        representativeEmployeeSection.push(
            {
                name: 'RepresentativeEmployeeId',
                component: 'Custom',
                render: ({ extraProps }) => <ITHeaderText label={t('representative', 'Representante')} value={extraProps.RepresentativeEmployeeName} />,
                extraProps: {
                    RepresentativeEmployeeName: data?.RepresentativeEmployee?.fullname_short
                }
            },
        )
    } else {
        representativeEmployeeSection.push(
            {
                name: 'RepresentativeEmployeeId',
                component: 'CustomSelectWithSearchBar',
                label: t('representative', 'Representante'),
                items: employees || {},
                onChange: getCompleteData,
                sortItems: true,
                fieldName: 'fullname_short',
                sortFunction: (emp) => sortEmployeesByUser(emp, false),
            },
        )
    }
    const sections: FormRender<WorkSheetModelType> = [
        {
            cols: 7,
            className: 'gap-x-20',
            elements: [
                {
                    name: 'full_assigned_number',
                    component: 'Custom',
                    render: ({ extraProps }) => <ITHeaderText label={t('assignedN', 'Nº asignado')} value={extraProps.full_assigned_number} className={(extraProps.full_assigned_number ? '' : 'text-red-BA')} />,
                    extraProps: {
                        full_assigned_number: data?.full_assigned_number
                    }
                },
                {
                    name: 'processed_date',
                    component: 'Custom',
                    render: ({ extraProps }) => <ITHeaderText label={t('date', 'Fecha')} value={displayDate(new Date(extraProps.processed_date))} />,
                    extraProps: {
                        processed_date: data?.processed_date
                    }
                },
                {
                    name: 'custom_name',
                    component: 'Custom',
                    render: (props) => {
                        return <InputWithLabelMargin name='custom_name' label={t('customName', 'Nombre personalizado')} className='custom_name_input_field' onChange={props.onChange} defaultValue={data.custom_name} />
                    }
                },
                ...representativeEmployeeSection,
                {
                    name: 'WorkingCenterId',
                    component: 'Custom',
                    render: ({ value }) => <ITHeaderText label={t('soldBy', 'Vendido por')} value={data?.WorkingCenter?.name || ''} />,
                    hideSectionByDefault: true
                },
                {
                    name: 'CustomerId',
                    component: 'Custom',
                    render: () => <ITHeaderTextCustomer label={t('customer', 'Cliente')} headerData={data} />,
                    hideSectionByDefault: true
                },
                {
                    name: 'BillingWorkingCenterId',
                    component: 'Custom',
                    render: ({ value }) => {
                        return <ITHeaderText label={t('billingBy', 'Facturado por')} value={data?.BillingWorkingCenter?.name || ''} />
                    },
                    hideSectionByDefault: true
                },
                {
                    name: 'WorkingCenterCounterId',
                    component: 'Custom',
                    render: ({ value }) => {
                        return <ITHeaderText label={t('serie', 'Serie')} value={data?.WorkingCenterCounter?.serie || ''} />
                    },
                    hideSectionByDefault: true
                },
                ...extraFields,
                {
                    name: 'WorkingCenterAddressId',
                    component: 'Custom',
                    render: ({ value, extraProps }) => {
                        return <ITHeaderText label={t('workingCenterAddress', 'Dirección centro de trabajo')} value={extraProps.workingCenterAddress} />
                    },
                    extraProps: {
                        workingCenterAddress: memorizedAddresses.workingCenterAddress
                    },
                    hideSectionByDefault: true
                },
                {
                    name: 'SellOrderId',
                    component: 'Custom',
                    render: ({ extraProps }) => <ITHeaderTextOrder label={t('sellOrderN', 'Nº pedido')} OrderId={extraProps.OrderId} full_asssigned_number={extraProps.full_assigned_number} orderUrl={locations.sellOrderSingle} readOnly={!permissionCheck(getUserPermissions('sell.order'), 'read')} />,
                    extraProps: {
                        full_assigned_number: data?.SellOrder?.full_assigned_number,
                        OrderId: data?.SellOrderId
                    },
                    hideSectionByDefault: true
                },
                {
                    name: 'SellDeliveryNoteId',
                    component: 'Custom',
                    render: ({ extraProps }) => <ITHeaderTextOrder label={t('sellDeliveryNoteN', 'Nº albarán')} OrderId={extraProps.OrderId} full_asssigned_number={extraProps.full_assigned_number} orderUrl={locations.sellDeliveryNoteSingle} readOnly={!permissionCheck(getUserPermissions('sell.deliveryNote'), 'read')} />,
                    extraProps: {
                        full_assigned_number: data?.SellDeliveryNote?.full_assigned_number,
                        OrderId: data?.SellDeliveryNoteId
                    },
                    hideSectionByDefault: true
                },
                {
                    name: 'SellInvoiceId',
                    component: 'Custom',
                    render: ({ extraProps }) => <ITHeaderTextOrder label={t('sellInvoiceN', 'Nº factura')} OrderId={extraProps.OrderId} full_asssigned_number={extraProps.full_assigned_number} orderUrl={locations.sellInvoiceSingle} readOnly={!permissionCheck(getUserPermissions('sell.invoice'), 'read')} />,
                    extraProps: {
                        full_assigned_number: data?.SellInvoice?.full_assigned_number,
                        OrderId: data?.SellInvoiceId
                    },
                    hideSectionByDefault: true
                },
            ]
        }
    ]
    const parsedFields = valueOrDefaultValue(sections, userPermissions, data)
    return (
        <InputTableTopHeader handleChange={myHandleChange} sections={parsedFields} headerData={data.header} getCompleteData={getCompleteData} />
    )
}

export default WorkSheetHeader